import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { addUserClaim, addUserClaimAttachments, getCompanyClaimTypes, getMyClaims } from '../../../api/Payroll/claim';
import { UserClaimInput, UserClaimStatus, UserClaimView, CompanyClaimTypeView } from '../../../models/payroll';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { CompanyCurrencyRate, Currency } from '../../../models/general';
import { GetCompanyRates } from '../../../api/General/currency';
import { getCompanyRules } from '../../../api/General/Company';
import { formatDate, formatNumber } from '../../../utils';
import { useFileUploadLimits } from '../../../hooks/useFileUploadLimits';
import CurrencyDropdown from '../../../components/currencyDropdown';
import Pagination from '../../../components/Pagination';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Spinner';
import AppIcon from '../../../components/AppIcon';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';

const PAGE_SIZE = 10;

interface FormState {
  claimId: string;
  amount: string;
  comments?: string;
  attachments: File[];
}

const MyClaims: React.FC = () => {
  const { t } = useTranslation();
  const company = useSelector((s: any) => s.user.company);
  const { toast, showToast, dismissToast } = useToast();
  const { validateFileSize } = useFileUploadLimits();

  // Data
  const [claims, setClaims] = useState<UserClaimView[]>([]);
  const [claimTypes, setClaimTypes] = useState<CompanyClaimTypeView[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<CompanyCurrencyRate[]>([]);
  const [, setBaseCurrency] = useState<Currency | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<any>(0);

  // Filters & pagination
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [yearMonth, setYearMonth] = useState<Date>(new Date());
  const [page, setPage] = useState(1);

  // UI state
  const [loading, setLoading] = useState(false);
  const [openClaimDialog, setOpenClaimDialog] = useState(false);

  // Form
  const [form, setForm] = useState<FormState>({ claimId: '', amount: '', comments: '', attachments: [] });
  const [formErrors, setFormErrors] = useState<{ amount?: string; claimId?: string }>({});

  // Fetch claim types (on demand when dialog opens)
  const loadClaimTypes = useCallback(async () => {
    if (claimTypes.length) return;
    try {
      const resp = await getCompanyClaimTypes();
      setClaimTypes(resp.payload || []);
    } catch (e) {
      console.error(e);
    }
  }, [claimTypes.length]);

  const loadData = useCallback(async (currencyId?: number) => {
    setLoading(true);
    try {
      const claimsResp = await getMyClaims(currencyId || 0);
      setClaims(claimsResp.payload || []);

      const [ratesResp, rulesResp] = await Promise.all([
        GetCompanyRates(),
        getCompanyRules(company?.id)
      ]);

      const rulesCurrency = rulesResp?.payload?.currency;
      setBaseCurrency(rulesCurrency);

      const merged: CompanyCurrencyRate[] = [];
      if (rulesCurrency) {
        const baseRate: CompanyCurrencyRate = {
          id: 0,
          // @ts-expect-error existing model fields alignment
          baseCurrency: rulesCurrency,
          baseCurrencyId: rulesCurrency.id,
          foreignCurrency: rulesCurrency,
          foreignCurrencyId: 0,
          company: company,
          companyId: company?.id,
          rate: 0,
          createdOn: new Date(),
          isArchived: false
        };
        merged.push(baseRate);
      }
      if (ratesResp?.payload) merged.push(...ratesResp.payload);
      setCurrencyOptions(merged);
    } catch (e) {
      console.error('Failed loading data', e);
      showToast(t('Failed to load data'), 'error');
    } finally {
      setLoading(false);
    }
  }, [company, t, showToast]);

  useEffect(() => {
    loadData(selectedCurrency?.id || 0);
  }, [loadData, selectedCurrency]);

  // Filtering
  const filteredClaims = useMemo(() => {
    return claims.filter(c => {
      const statusOk = statusFilter === 'All' || c.status === statusFilter;
      const d = new Date(c.requestDate);
      const dateOk = d.getFullYear() === yearMonth.getFullYear() && d.getMonth() === yearMonth.getMonth();
      return statusOk && dateOk;
    });
  }, [claims, statusFilter, yearMonth]);

  const totalPages = Math.max(1, Math.ceil(filteredClaims.length / PAGE_SIZE));
  const pagedClaims = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredClaims.slice(start, start + PAGE_SIZE);
  }, [filteredClaims, page]);

  // Derived summary metrics
  const summary = useMemo(() => {
    const initial = { total: 0, approved: 0, pending: 0, rejected: 0 };
    return filteredClaims.reduce((acc, c) => {
      const amt = parseFloat(c.amount || '0') || 0;
      acc.total += amt;
      if (c.status === UserClaimStatus.Approved) acc.approved += amt;
      else if (c.status === UserClaimStatus.Pending) acc.pending += amt;
      else if (c.status === UserClaimStatus.Rejected) acc.rejected += amt;
      return acc;
    }, initial);
  }, [filteredClaims]);

  const resetFilters = () => {
    setStatusFilter('All');
    setYearMonth(new Date());
  };

  // Form helpers
  const validateForm = (): boolean => {
    const errors: { amount?: string; claimId?: string } = {};
    if (!form.claimId) errors.claimId = t('Claim type required');
    if (!form.amount || parseFloat(form.amount) <= 0) errors.amount = t('Enter a valid amount');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const input: UserClaimInput = {
        id: 0,
        amount: form.amount,
        companyClaimTypeId: Number(form.claimId),
        status: UserClaimStatus.Pending,
        comments: form.comments
      };
      const resp = await addUserClaim(input);
      if (resp.status === 200 && resp.payload?.id && form.attachments.length) {
        await addUserClaimAttachments(resp.payload.id, form.attachments);
      }
      showToast(t('Claim submitted'), 'success');
      setOpenClaimDialog(false);
      setForm({ claimId: '', amount: '', comments: '', attachments: [] });
      await loadData(selectedCurrency?.id || 0);
    } catch (e: any) {
      console.error(e);
      showToast(e?.message || t('Submission failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const statusBadgeVariant = (status: string) => {
    switch (status) {
      case UserClaimStatus.Approved: return 'success' as const;
      case UserClaimStatus.Rejected: return 'error' as const;
      case UserClaimStatus.Pending: return 'warning' as const;
      default: return 'neutral' as const;
    }
  };

  return (
    <PageLayout
      title={t('My Claims')}
      icon={Receipt}
      accentColor="emerald"
      maxWidth="1600px"
      headerActions={
        <Button
          variant="outline"
          onClick={() => { setOpenClaimDialog(true); loadClaimTypes(); }}
          data-testid="add-claim"
        >
          {t('REQUEST CLAIM')}
        </Button>
      }
    >
    <div className="relative flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          iconName={showFilters ? 'Minus' : 'Plus'}
          onClick={() => setShowFilters(s => !s)}
        >
          {t('Filter')}
        </Button>
        <CurrencyDropdown
          value={selectedCurrency}
          options={currencyOptions}
          onOptionChange={(option: any) => setSelectedCurrency(option)}
        />
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="transition-all duration-200 rounded-xl border border-border bg-card p-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2" data-testid="claim-status-filter">
            <span className="text-sm font-semibold text-foreground">{t('Status')}</span>
            <div className="flex items-center gap-4 flex-wrap">
              {[
                { value: 'All', label: 'All' },
                { value: UserClaimStatus.Pending, label: 'Pending' },
                { value: UserClaimStatus.Rejected, label: 'Rejected' },
                { value: UserClaimStatus.Approved, label: 'Approved' },
              ].map(opt => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="statusFilter"
                    value={opt.value}
                    checked={statusFilter === opt.value}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className="text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t(opt.label)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <YearMonthPicker
              date={yearMonth}
              onChange={(d: Date) => { setYearMonth(d); setPage(1); }}
              fromMonth={new Date(new Date().getFullYear() - 8, 0, 1)}
              toMonth={new Date(new Date().getFullYear() + 1, 11, 1)}
              isRtl={false}
            />
            <Button variant="ghost" size="sm" onClick={resetFilters}>{t('Reset')}</Button>
          </div>
        </div>
      )}

      {/* Summary Bar */}
      <div className="flex flex-end flex-wrap gap-2 pt-2">
        <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
          {t('Total')}: {formatNumber(summary.total)}
        </span>
        <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          {t('Approved')}: {formatNumber(summary.approved)}
        </span>
        <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700">
          {t('Pending')}: {formatNumber(summary.pending)}
        </span>
        <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-50 px-3 py-1 text-sm font-medium text-red-700">
          {t('Rejected')}: {formatNumber(summary.rejected)}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="claims-list">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Claim Id')}</TableHead>
              <TableHead>{t('Date Requested')}</TableHead>
              <TableHead>{t('Status')}</TableHead>
              <TableHead className="text-end">{t('Amount')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedClaims.map(row => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link to={`/payroll/claims/${row.id}`} className="font-semibold underline text-primary hover:text-primary/80">
                    {row.id}
                  </Link>
                </TableCell>
                <TableCell>{formatDate(new Date(row.requestDate))}</TableCell>
                <TableCell>
                  <StatusBadge variant={statusBadgeVariant(row.status)} size="sm">
                    {t(row.status)}
                  </StatusBadge>
                </TableCell>
                <TableCell className="text-end">{formatNumber(parseFloat(row.amount))} {row.currency?.currencyCode || ''}</TableCell>
              </TableRow>
            ))}
            {!pagedClaims.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className="flex items-center justify-center py-8">
                    <span className="text-sm text-muted-foreground">{t('No claims found')}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-end p-3">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      </div>

      {/* Claim Dialog */}
      <Dialog open={openClaimDialog} onOpenChange={(open) => { if (!open) setOpenClaimDialog(false); }}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{t('REQUEST CLAIM')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Select
              searchable
              options={claimTypes.map(c => ({ value: c.id.toString(), label: c.name }))}
              value={form.claimId}
              onChange={(val: any) => setForm(f => ({ ...f, claimId: val?.toString() || '' }))}
              label={t('Claim')}
              error={formErrors.claimId}
              placeholder={t('Select')}
            />
            <Input
              label={t('Amount')}
              type="number"
              value={form.amount}
              onChange={(e: any) => setForm(f => ({ ...f, amount: e.target.value }))}
              error={formErrors.amount}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Comments')}</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[60px]"
                value={form.comments}
                onChange={(e) => setForm(f => ({ ...f, comments: e.target.value }))}
                rows={3}
              />
            </div>
            <Button
              variant="outline"
              className="self-start"
              onClick={() => {
                const fileInput = document.getElementById('claim-file-input');
                if (fileInput) fileInput.click();
              }}
            >
              <AppIcon name="Paperclip" size={16} className="me-2" />
              {form.attachments.length
                ? t('Files selected', { count: form.attachments.length, defaultValue: 'Files selected: {{count}}' })
                : t('Attach files')}
            </Button>
            <input
              id="claim-file-input"
              hidden
              type="file"
              multiple
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                const oversized = files.filter((f) => !validateFileSize(f, 'claimAttachmentMaxSizeMB').valid);
                if (oversized.length > 0) {
                  const { maxMB } = validateFileSize(oversized[0], 'claimAttachmentMaxSizeMB');
                  showToast(t('fileSizeExceedsLimit', { name: oversized[0].name, maxMB }), 'error');
                  e.target.value = '';
                  return;
                }
                setForm(f => ({ ...f, attachments: files }));
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenClaimDialog(false)}>
              {t('Cancel')}
            </Button>
            <Button variant="default" loading={loading} onClick={handleSubmit}>
              {t('REQUEST')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/55">
          <Spinner size="lg" />
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
    </PageLayout>
  );
};

export default MyClaims;
